import React, { useEffect, useMemo, useState } from 'react';
import { GripVertical, Link2 } from 'lucide-react';
import { SchemaMappingBlock, SchemaMappingTargetActions, SchemaMappingJoinedSources, SchemaMappingPanels, SchemaMappingSeparatorEditor,
  type SchemaMappingBlockTarget } from './SchemaMappingWizard';
import { emptyOutboundRequestMappingDocument, parseOutboundRequestMappingDocument, serializeOutboundRequestMappingDocument,
  setOutboundRequestMapping, setOutboundRequestTargetRequired, withPreservedMaxLength, OUTBOUND_MAPPING_MAX_LENGTH,
  type OutboundRequestFieldMapping } from './outboundRequestMapping';
import './InboundSchemaFieldMapper.css';
import { mappingTargetState, transitionMappingTarget, type MappingTargetStateInput } from './schemaMappingState';

type InputCategory = 'Common' | 'Alert' | 'Notification' | 'Integration' | 'Payload' | 'Steps';
type InputField = { path: string; category: InputCategory; type: string; integrationId?: string };
export interface OutboundPayloadVariable { path: string; sourceName: string; type: string; integrationId?: string }
type OutputField = { pointer: string; type: string; required: boolean };
type RequestFieldMapping = OutboundRequestFieldMapping;
const dragType = 'application/x-zurvit-outbound-source';
const filterCategories = ['All', 'Common', 'Alert', 'Notification', 'Integration', 'Payload', 'Steps'] as const;

const alertFields = ['id', 'title', 'description', 'severity', 'status', 'service', 'source', 'assignee', 'resolution',
  'externalalertid', 'externalticketid', 'correlationid', 'occurredat', 'createdat', 'tenantid', 'inboundintegrationid'];
const notificationFields = ['id', 'title', 'description', 'severity', 'status', 'service', 'source', 'correlationid', 'occurredat'];
const stepOutputs = ['response', 'responseBody', 'responseStatusCode', 'attempts', 'success', 'actionKey', 'ticketId', 'notified', 'attempted', 'errors'];

function pointerSegment(value: string): string { return value.replace(/~/g, '~0').replace(/\//g, '~1'); }

export function outboundSchemaFields(schemaJson: string, requiredTargets?: ReadonlySet<string>): OutputField[] {
  const root = JSON.parse(schemaJson) as Record<string, unknown>;
  const fields: OutputField[] = [];
  let visits = 0;
  const visit = (node: unknown, pointer: string, schemaRequired: boolean, depth: number) => {
    if (++visits > 2000 || depth > 20 || !node || typeof node !== 'object' || Array.isArray(node)) return;
    const schema = node as Record<string, unknown>;
    const properties = schema.properties;
    if (properties && typeof properties === 'object' && !Array.isArray(properties)) {
      const requiredNames = new Set(Array.isArray(schema.required) ? schema.required.filter((name): name is string => typeof name === 'string') : []);
      for (const [key, child] of Object.entries(properties as Record<string, unknown>)) {
        visit(child, `${pointer}/${pointerSegment(key)}`, requiredNames.has(key), depth + 1);
      }
      return;
    }
    if (schema.type === 'array' && schema.items && typeof schema.items === 'object') {
      visit(schema.items, `${pointer}/0`, schemaRequired, depth + 1);
      return;
    }
    if (pointer) fields.push({
      pointer,
      type: typeof schema.type === 'string' ? schema.type : Array.isArray(schema.type) ? schema.type.join(' | ') : 'unspecified',
      required: requiredTargets ? requiredTargets.has(pointer) : schemaRequired,
    });
  };
  visit(root, '', false, 0);
  return fields;
}

function buildInputFields(samplePayloadTokens: string[], outputs: OutputField[]): InputField[] {
  const fields: InputField[] = [];
  const add = (path: string, category: InputCategory, type = 'string') => fields.push({ path, category, type });
  for (const field of alertFields) add(`alert.${field}`, 'Alert');
  for (const field of notificationFields) add(`notification.${field}`, 'Notification');
  for (const field of ['id', 'type', 'title', 'description', 'severity', 'status', 'service', 'source', 'correlationid']) add(`event.${field}`, 'Common');
  for (const field of ['id', 'name', 'provider', 'version', 'ticketId']) add(`integration.${field}`, 'Integration');
  add('alert.data.normalisedPayload', 'Payload', 'object');
  add('notification.data.normalisedPayload', 'Payload', 'object');
  add('event.data.normalisedPayload', 'Payload', 'object');

  for (const output of outputs) {
    const leaf = output.pointer.split('/').pop()?.replace(/~1/g, '/').replace(/~0/g, '~');
    if (leaf && /^[A-Za-z0-9_.-]+$/.test(leaf)) add(`integration.fields.${leaf}`, 'Integration', output.type);
  }

  for (const token of samplePayloadTokens) {
    const path = normalizeVariablePath(token.replace(/^\{\{\s*|\s*\}\}$/g, ''));
    if (!/^[A-Za-z0-9_.-]+$/.test(path) || path.startsWith('secret.')) continue;
    const isNormalizedPayloadPath = path.includes('.data.normalisedPayload');
    add(path, !isNormalizedPayloadPath && path.startsWith('notification.') ? 'Notification' : 'Payload');
    const normalizedPayloadPrefix = 'alert.data.normalisedPayload.';
    if (path.startsWith(normalizedPayloadPrefix)) {
      const nestedPath = path.slice(normalizedPayloadPrefix.length);
      add(`notification.data.normalisedPayload.${nestedPath}`, 'Payload');
      add(`event.data.normalisedPayload.${nestedPath}`, 'Payload');
    } else if (path.startsWith('alert.data.')) {
      const nestedPath = path.replace(/^alert\.data\./, '');
      add(`notification.data.${nestedPath}`, 'Payload');
      add(`event.data.normalisedPayload.${nestedPath}`, 'Payload');
    }
  }

  for (let order = 1; order <= 8; order++) {
    for (const field of stepOutputs) add(`step.${order}.${field}`, 'Steps');
  }
  return Array.from(new Map(fields.map(field => [field.path, field])).values());
}

function normalizeVariablePath(path: string): string {
  return path.replace(/\[(\d+)\]/g, '.$1').replace(/\.+/g, '.').replace(/^\./, '');
}

function readMappingDocument(value: string) {
  try { return parseOutboundRequestMappingDocument(value || ''); }
  catch { return emptyOutboundRequestMappingDocument(); }
}

function suggestedSource(pointer: string, fields: InputField[]): string {
  const leaf = (pointer.split('/').pop() || '').replace(/~1/g, '/').replace(/~0/g, '~').toLowerCase();
  const aliases: Record<string, string> = {
    title: 'alert.title', summary: 'alert.title', short_description: 'alert.title',
    description: 'alert.description', details: 'alert.description', severity: 'alert.severity',
    status: 'alert.status', service: 'alert.service', source: 'alert.source',
    correlationid: 'alert.correlationid', externalalertid: 'alert.externalalertid', ticketid: 'integration.ticketId',
  };
  const candidate = aliases[leaf] || `alert.data.${leaf}`;
  return fields.some(field => field.path === candidate) ? candidate : '';
}

export interface OutboundActionFieldMapperProps {
  requestSchemaJson: string;
  requestMappingsJson: string;
  samplePayloadTokens?: string[];
  integrationVariables?: string[];
  activePayloadVariables?: OutboundPayloadVariable[];
  onChange: (requestMappingsJson: string) => void;
}

export const OutboundActionFieldMapper: React.FC<OutboundActionFieldMapperProps> = ({
  requestSchemaJson,
  requestMappingsJson,
  samplePayloadTokens = [],
  integrationVariables = [],
  activePayloadVariables = [],
  onChange,
}) => {
  const [filter, setFilter] = useState<(typeof filterCategories)[number]>('All');
  const [payloadIntegration, setPayloadIntegration] = useState('');
  const [search, setSearch] = useState('');
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [staticTarget, setStaticTarget] = useState<string | null>(null);
  const [staticValue, setStaticValue] = useState('');

  let outputs: OutputField[] = [];
  let schemaError = '';
  try {
    outputs = requestSchemaJson.trim() ? outboundSchemaFields(requestSchemaJson, new Set(readMappingDocument(requestMappingsJson).requiredTargets)) : [];
  } catch {
    schemaError = 'Enter a valid outbound request schema before mapping fields.';
  }
  const inputs = useMemo<InputField[]>(() => {
    const base = buildInputFields(samplePayloadTokens, outputs);
    const extras = integrationVariables
      .map(path => normalizeVariablePath(path.trim()))
      .filter(path => /^[A-Za-z][A-Za-z0-9_.-]*$/.test(path) && path.startsWith('integration.'))
      .map(path => ({ path, category: 'Integration' as const, type: 'string' }));
    const activePayload = activePayloadVariables
      .map(variable => ({
        path: normalizeVariablePath(variable.path),
        category: 'Payload' as const,
        type: `${variable.type} · ${variable.sourceName}`,
        integrationId: variable.integrationId || variable.sourceName,
      }))
      .filter(field => /^[A-Za-z][A-Za-z0-9_.-]*$/.test(field.path) && !field.path.startsWith('secret.'));
    return Array.from(new Map([...base, ...extras, ...activePayload].map(field => [field.path, field])).values());
  }, [samplePayloadTokens, outputs, integrationVariables, activePayloadVariables]);
  const payloadIntegrations = Array.from(new Map(activePayloadVariables.map(variable => [
    variable.integrationId || variable.sourceName, variable.sourceName,
  ])).entries()).sort((left, right) => left[1].localeCompare(right[1]));
  const effectivePayloadIntegration = payloadIntegrations.some(([id]) => id === payloadIntegration) ? payloadIntegration : '';
  useEffect(() => {
    if (payloadIntegration && !effectivePayloadIntegration) setPayloadIntegration('');
  }, [payloadIntegration, effectivePayloadIntegration]);
  const mappingDocument = readMappingDocument(requestMappingsJson);
  const mappings = mappingDocument.mappings;
  const inputMatchesFilters = (field: InputField, searchTerm = search) => (filter === 'All' || field.category === filter)
    && (filter !== 'Payload' || !effectivePayloadIntegration || field.integrationId === effectivePayloadIntegration)
    && `${field.path} ${field.type}`.toLowerCase().includes(searchTerm.trim().toLowerCase());
  const visibleInputs = inputs.filter(field => inputMatchesFilters(field));
  const update = (target: string, mapping?: RequestFieldMapping) => {
    const next = withPreservedMaxLength(mapping, mappings[target]);
    onChange(serializeOutboundRequestMappingDocument(setOutboundRequestMapping(mappingDocument, target, next)));
  };

  const setMaxLength = (target: string, raw: string) => {
    const current = mappings[target];
    if (!current || 'value' in current) return;
    const { maxLength: _previous, ...rest } = current;
    const parsed = Number.parseInt(raw, 10);
    const next = raw.trim() === '' || !Number.isFinite(parsed)
      ? rest as RequestFieldMapping
      : { ...rest, maxLength: Math.min(Math.max(parsed, 1), OUTBOUND_MAPPING_MAX_LENGTH) } as RequestFieldMapping;
    onChange(serializeOutboundRequestMappingDocument(setOutboundRequestMapping(mappingDocument, target, next)));
  };

  const stateInput = (target: string): MappingTargetStateInput => {
    const mapping = mappings[target];
    const supportsText = outputs.find(output => output.pointer === target)?.type.split('|').some(type => type.trim() === 'string');
    return { selected: Object.prototype.hasOwnProperty.call(mappings, target), required: mappingDocument.requiredTargets.includes(target),
      value: !mapping ? null : 'source' in mapping ? { kind: 'source', source: mapping.source }
        : 'sources' in mapping ? { kind: 'join', sources: mapping.sources } : { kind: 'static' },
      supportsJoin: supportsText, supportsStatic: true };
  };

  const updateRequired = (target: string, required: boolean) => {
    const next = transitionMappingTarget(stateInput(target), { type: 'required', required });
    const nextDocument = setOutboundRequestMapping(mappingDocument, target,
      next.selected ? mappings[target] ?? { source: '' } : undefined);
    onChange(serializeOutboundRequestMappingDocument(setOutboundRequestTargetRequired(nextDocument, target, required)));
  };

  const appendJoinSource = (target: string, source: string) => {
    const current = mappings[target];
    const joined = current && 'sources' in current ? current : { sources: [], combine: 'join' as const, separator: ' ' };
    if (joined.sources.includes(source) || joined.sources.length >= 32) return;
    update(target, { ...joined, sources: [...joined.sources, source] });
  };

  const editStaticValue = (pointer: string, mapping?: RequestFieldMapping) => {
    setStaticTarget(pointer);
    setStaticValue(mapping && 'value' in mapping ? mapping.value : '');
  };

  const applyStaticValue = (pointer: string) => {
    update(pointer, { value: staticValue });
    setStaticTarget(null);
  };
  const sourceItems = visibleInputs.map(field => ({
    id: field.path,
    label: `{{${field.path}}}`,
    details: `${field.category} · ${field.type}`,
    searchText: `${field.path} ${field.type}`,
    mappedTargets: Object.entries(mappings).filter(([, mapping]) => 'source' in mapping ? mapping.source === field.path
      : 'sources' in mapping && mapping.sources.includes(field.path)).map(([target]) => target),
  }));
  const targetItems: SchemaMappingBlockTarget[] = outputs.map(output => {
    const controls = mappingTargetState(stateInput(output.pointer));
    return { id: output.pointer, label: output.pointer, details: output.type, data: output, selectable: true,
      enabled: controls.selected,
      requiredToggle: true, required: controls.required,
      state: controls.state, className: 'outbound-action-mapped-cell' };
  });

  return <div className="inbound-field-mapper outbound-action-field-mapper" aria-label="Outbound action field mapping">
    <div className="outbound-action-mapping-toolbar">
      <label>Filter input variables<input type="search" value={search} onChange={event => {
        const nextSearch = event.target.value;
        setSearch(nextSearch);
        if (selectedSource && !inputs.some(field => field.path === selectedSource && inputMatchesFilters(field, nextSearch)))
          setSelectedSource(null);
      }} /></label>
      <div className="workflow-content-segments" role="group" aria-label="Input variable filter">
        {filterCategories.map(category => <button type="button" key={category} aria-pressed={filter === category} onClick={() => setFilter(category)}>{category}</button>)}
      </div>
      {filter === 'Payload' && <label>Payload integration<select aria-label="Payload integration" value={effectivePayloadIntegration}
        onChange={event => setPayloadIntegration(event.target.value)}>
        <option value="">All integrations</option>
        {payloadIntegrations.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
      </select></label>}
    </div>
    <SchemaMappingBlock ariaLabel="Outbound request mapping" sources={sourceItems} targets={targetItems} dragType={dragType}
      selectedSource={selectedSource} sourceHeading="Input fields / variables" sourceTableLabel="Outbound mapping input variables"
      targetHeading="Outbound fields" targetTableLabel="Outbound request schema target fields" mappedHeading="Mapped input"
      actionsHeading="Mapping actions" actionsClassName="outbound-action-mapping-controls"
      sourceEmpty="No input variables match this filter." targetEmpty={schemaError || 'No selectable scalar fields in the outbound schema.'}
      targetTableClassName="outbound-action-mapping-table" sourceScrollClassName="outbound-action-source-scroll"
      targetScrollClassName="outbound-action-target-scroll" onSelectSource={setSelectedSource}
      onSetTargetEnabled={(pointer, enabled) => {
        const next = transitionMappingTarget(stateInput(pointer), { type: 'select', selected: enabled });
        if (enabled) update(pointer, mappings[pointer] ?? { source: suggestedSource(pointer, inputs) });
        else if (!next.selected) {
          update(pointer);
          if (staticTarget === pointer) setStaticTarget(null);
        }
      }}
      onSetTargetRequired={updateRequired}
      canDrop={(source, target) => !mappingTargetState(stateInput(target)).mappingDisabled && inputs.some(field => field.path === source)}
      onDropSource={(source, target) => {
        const mapping = mappings[target];
        if (mapping && 'sources' in mapping) appendJoinSource(target, source);
        else update(target, { source });
      }}
      renderTargetHeading={target => {
        const output = target.data as OutputField;
        return <>
          <small>{output.type}</small>
        </>;
      }}
      renderTarget={(target, selected) => {
        const output = target.data as OutputField;
        const pointer = output.pointer;
        const checked = mappingTargetState(stateInput(pointer)).selected;
        const mapping = mappings[pointer];
        const joined = mapping && 'sources' in mapping ? mapping : null;
        const mapped = mapping && 'source' in mapping ? mapping.source : '';
        const mappedStaticValue = mapping && 'value' in mapping ? mapping.value : '';
        const hasStaticValue = !!mapping && 'value' in mapping;
        return <>
          <button type="button" className="outbound-action-mapped-value" disabled={!checked || selected === null}
            aria-label={selected ? `${joined ? 'Add' : 'Map'} ${selected} to ${pointer}` : `Map to ${pointer}`}
            title={`${joined ? 'Add' : 'Map'} a selected or dragged input variable for ${pointer}`}
            onClick={() => selected && checked && (joined ? appendJoinSource(pointer, selected) : update(pointer, { source: selected }))}>
            <span className="outbound-action-mapped-icon"><Link2 size={16} aria-hidden="true" /></span>
            <span className="inbound-field-mapper-connection">{joined
              ? joined.sources.length ? joined.sources.map(source => `{{${source}}}`).join(joined.separator ?? ' ') : 'Select or drag sources to join'
              : mapped ? `{{${mapped}}}` : hasStaticValue ? `Static: ${mappedStaticValue}` : checked ? 'Select or drag an input' : 'Not selected'}</span>
          </button>
          {joined && <div className="outbound-action-joined-sources" aria-label={`Joined sources for ${pointer}`}>
            <SchemaMappingJoinedSources label={`Joined sources for ${pointer}`} className="outbound-action-joined-sources-list"
              sources={joined.sources} sourceLabel={source => `{{${source}}}`}
              removeLabel={source => `Remove ${source} from ${pointer}`}
              onRemove={source => update(pointer, { ...joined, sources: joined.sources.filter(item => item !== source) })}
              onReorder={sources => update(pointer, { ...joined, sources })} />
            <SchemaMappingSeparatorEditor presetLabel={`Separator preset for ${pointer}`}
              valueLabel={`Join separator for ${pointer}`} value={joined.separator}
              onChange={separator => update(pointer, { ...joined, separator })} />
          </div>}
          {checked && mapping && !('value' in mapping) && <label className="outbound-action-max-length">
            Max length
            <input type="number" min={1} max={OUTBOUND_MAPPING_MAX_LENGTH} step={1} inputMode="numeric"
              aria-label={`Max length for ${pointer}`} placeholder="No limit"
              value={mapping.maxLength ?? ''} onChange={event => setMaxLength(pointer, event.target.value)} />
            <small>Longer values are truncated before sending.</small>
          </label>}
          {staticTarget === pointer && <div className="outbound-action-static-editor">
            <input aria-label={`Static value for ${pointer}`} type="text" value={staticValue}
              onChange={event => setStaticValue(event.target.value)} placeholder="Enter literal text" />
            <button type="button" className="btn-secondary btn-sm" aria-label={`Apply static value to ${pointer}`}
              onClick={() => applyStaticValue(pointer)}>Apply</button>
            <button type="button" className="btn-secondary btn-sm" aria-label={`Cancel static value for ${pointer}`}
              onClick={() => setStaticTarget(null)}>Cancel</button>
          </div>}
        </>;
      }}
      renderActions={target => {
        const output = target.data as OutputField;
        const mapping = mappings[output.pointer];
        const joined = mapping && 'sources' in mapping ? mapping : null;
        const mapped = mapping && 'source' in mapping ? mapping.source : '';
        const hasStaticValue = !!mapping && 'value' in mapping;
        const controls = mappingTargetState(stateInput(output.pointer));
        return <SchemaMappingTargetActions controls={controls}
            joinLabel={`${joined ? 'Use single source' : 'Join text'} for ${output.pointer}`}
            joinTitle={joined ? 'Use a single source' : 'Join text'}
            onJoin={() => {
              if (joined) update(output.pointer, joined.sources.length ? { source: joined.sources[0] }
                : transitionMappingTarget(stateInput(output.pointer), { type: 'remove' }).selected ? { source: '' } : undefined);
              else update(output.pointer, { sources: !hasStaticValue && mapped ? [mapped] : [], combine: 'join', separator: ' ' });
            }}
          staticLabel={`${hasStaticValue ? 'Edit' : 'Set'} static value for ${output.pointer}`}
          staticTitle="Set static text for this request field"
          onStatic={() => editStaticValue(output.pointer, mapping)}
          removeLabel={`Remove mapping for ${output.pointer}`} onRemove={() => {
            const next = transitionMappingTarget(stateInput(output.pointer), { type: 'remove' });
            update(output.pointer, next.selected ? { source: '' } : undefined);
            if (staticTarget === output.pointer) setStaticTarget(null);
          }} />;
      }} />
  </div>;
};

type ResponseTarget = { path: string; category: 'Alert' | 'Notification' | 'Integration' | 'Artifact'; source: string };

function responseTargetCategory(path: string): ResponseTarget['category'] {
  if (path.startsWith('artifact.')) return 'Artifact';
  if (path.startsWith('alert.')) return 'Alert';
  if (path.startsWith('notification.')) return 'Notification';
  return 'Integration';
}

function responseTargets(fields: OutputField[]): ResponseTarget[] {
  const targets = new Map<string, ResponseTarget>();
  for (const field of fields) {
    const leaf = (field.pointer.split('/').pop() || 'value').replace(/~1/g, '/').replace(/~0/g, '~');
    const tokenLeaf = leaf.replace(/[^A-Za-z0-9_.-]/g, '_');
    const normalizedLeaf = tokenLeaf.toLowerCase();
    const integrationPath = ['id', 'ticketid', 'ticketnumber', 'ticket_number', 'externalticketid'].includes(normalizedLeaf)
      ? 'integration.ticketId' : `integration.${tokenLeaf}`;
    const fieldTargets: Array<{ path: string; category: ResponseTarget['category'] }> = [
      { path: `alert.data.${tokenLeaf}`, category: 'Alert' as const },
      { path: `notification.data.${tokenLeaf}`, category: 'Notification' as const },
    ];
    if (integrationPath !== 'integration.ticketId')
      fieldTargets.push({ path: integrationPath, category: 'Integration' });
    for (const target of fieldTargets) {
      if (!targets.has(target.path)) targets.set(target.path, { ...target, source: field.pointer });
    }
  }
  return Array.from(targets.values());
}

function parseResponseMappings(value: string): Record<string, string> {
  try {
    const parsed = JSON.parse(value || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const validSource = (source: string) => source.startsWith('/') || source === '$' ||
      (source.startsWith('$.') && source.slice(2).split('.').every(segment => {
        if (!segment) return false;
        const bracket = segment.indexOf('[');
        if (bracket < 0) return true;
        return bracket > 0 && /^\d+\]$/.test(segment.slice(bracket + 1));
      }));
    const entries = Object.entries(parsed).filter((entry): entry is [string, string] =>
      /^[A-Za-z][A-Za-z0-9_.-]*$/.test(entry[0]) && typeof entry[1] === 'string' && entry[1].length <= 1024 && validSource(entry[1]));
    return Object.fromEntries(entries);
  } catch { return {}; }
}

export function OutboundActionResponseMapper({ responseSchemaJson, responseMappingsJson, onChange }: {
  responseSchemaJson: string;
  responseMappingsJson: string;
  onChange: (responseMappingsJson: string) => void;
}) {
  const [filter, setFilter] = useState<'All' | 'Alert' | 'Notification' | 'Integration' | 'Artifact'>('All');
  const [search, setSearch] = useState('');
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [customTargetPath, setCustomTargetPath] = useState('');
  const [customTargetError, setCustomTargetError] = useState<string | null>(null);
  let fields: OutputField[] = [];
  let invalidSchema = false;
  try { if (responseSchemaJson.trim()) fields = outboundSchemaFields(responseSchemaJson); }
  catch { invalidSchema = true; }
  const mappings = parseResponseMappings(responseMappingsJson);
  const sourceFields: OutputField[] = [{ pointer: '/', type: 'object', required: true }, ...fields];
  const targets = useMemo(() => {
    const map = new Map<string, ResponseTarget>();
    for (const target of responseTargets(fields)) {
      map.set(target.path, target);
    }
    for (const [path, pointer] of Object.entries(mappings)) {
      if (!map.has(path)) {
        map.set(path, { path, category: responseTargetCategory(path), source: pointer || '/' });
      }
    }
    return Array.from(map.values());
  }, [fields, mappings]);
  const visibleTargets = targets.filter(target => (filter === 'All' || target.category === filter)
    && target.path.toLowerCase().includes(search.trim().toLowerCase()));
  const update = (target: string, source: string | null) => {
    const next = { ...mappings };
    if (source === null) delete next[target];
    else next[target] = source;
    onChange(JSON.stringify(next));
  };

  const addCustomTarget = () => {
    const target = normalizeVariablePath(customTargetPath.trim());
    if (!target) {
      setCustomTargetError('Enter a destination variable path.');
      return;
    }
    const artifactTarget = target.startsWith('artifact.');
    if (!/^[A-Za-z][A-Za-z0-9_.-]*$/.test(target) || target.startsWith('secret.') ||
      artifactTarget && !/^artifact\.(json|text|link|field|image)\.[A-Za-z][A-Za-z0-9_.-]*$/i.test(target)) {
      setCustomTargetError('Destination must be a non-secret workflow variable path (for example D365TKT or alert.data.ticketId).');
      return;
    }
    if (target.toLowerCase() === 'integration.ticketid') {
      setCustomTargetError('integration.ticketId is provided by Ticket ID Variable; choose a different response destination.');
      return;
    }

    setCustomTargetError(null);
    update(target, selectedSource || mappings[target] || '/');
    setCustomTargetPath('');
  };

  return <div className="inbound-field-mapper outbound-action-field-mapper" aria-label="Outbound response mapping">
    <div className="outbound-action-mapping-toolbar">
      <label>Filter destination variables<input type="search" value={search} onChange={event => setSearch(event.target.value)} /></label>
      <div className="workflow-content-segments" role="group" aria-label="Response variable destination filter">
        {(['All', 'Alert', 'Notification', 'Integration', 'Artifact'] as const).map(category => <button type="button" key={category}
          aria-pressed={filter === category} onClick={() => setFilter(category)}>{category}</button>)}
      </div>
    </div>
    <div className="outbound-action-mapping-toolbar" style={{ marginTop: 8 }}>
      <label>Create variable
        <input aria-label="Custom response destination variable" type="text" value={customTargetPath}
          onChange={event => { setCustomTargetPath(event.target.value); if (customTargetError) setCustomTargetError(null); }}
          placeholder="D365TKT" />
      </label>
      <button type="button" className="btn-secondary btn-sm" onClick={addCustomTarget}>Create variable</button>
    </div>
    {customTargetError && <p className="error-message" role="alert" style={{ marginTop: 6 }}>{customTargetError}</p>}
    <p className="help-text" style={{ marginTop: 6 }}>Select a response field or use JSONPath such as <code>$.ticket.id</code>. Use <code>artifact.image.name</code>, <code>artifact.json.name</code>, <code>artifact.text.name</code>, <code>artifact.link.name</code> or <code>artifact.field.name</code> to persist bounded data with the alert; source <strong>/</strong> maps the full provider payload.</p>
    <SchemaMappingPanels>
      <div className="inbound-field-mapper-input-scroll"><table aria-label="Provider response fields">
        <thead><tr><th scope="col">Response schema fields</th></tr></thead>
        <tbody>{sourceFields.map(field => <tr key={field.pointer}><td>
          <button type="button" className={selectedSource === field.pointer ? 'inbound-field-mapper-source mapped' : 'inbound-field-mapper-source'}
            aria-label={`Select response ${field.pointer}`} aria-pressed={selectedSource === field.pointer} draggable
            onClick={() => setSelectedSource(field.pointer)}
            onDragStart={event => { event.dataTransfer.effectAllowed = 'copy'; event.dataTransfer.setData(dragType, field.pointer); setSelectedSource(field.pointer); }}>
            <GripVertical size={16} aria-hidden="true" /><span><strong>{field.pointer}</strong><small>{field.pointer === '/' ? 'entire response payload' : `${field.type}${field.required ? ' / required' : ''}`}</small></span>
          </button>
        </td></tr>)}
          {!sourceFields.length && <tr><td>{invalidSchema ? 'Enter a valid response schema.' : 'Paste a provider response schema to map response fields.'}</td></tr>}
        </tbody>
      </table></div>
      <div className="inbound-field-mapper-input-scroll"><table aria-label="Response workflow variable destinations">
        <colgroup><col className="inbound-field-mapper-output-track" /><col /></colgroup>
        <thead><tr><th scope="col">Alert / notification / integration variables</th><th scope="col">Mapped response field</th></tr></thead>
        <tbody>{visibleTargets.map(target => {
          const selected = Object.prototype.hasOwnProperty.call(mappings, target.path);
          const source = mappings[target.path] || '';
          return <tr key={target.path}>
            <th scope="row"><label><input type="checkbox" aria-label={`Select destination ${target.path}`} checked={selected}
              onChange={event => update(target.path, event.target.checked ? source || target.source : null)} /> <strong>{`{{${target.path}}}`}</strong></label><small>{target.category}</small></th>
            <td className="inbound-field-mapper-target" onDragOver={event => {
              if (selected && Array.from(event.dataTransfer.types).includes(dragType)) { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }
            }} onDrop={event => {
              event.preventDefault();
              const pointer = event.dataTransfer.getData(dragType);
              if (selected && sourceFields.some(field => field.pointer === pointer)) update(target.path, pointer);
            }}>
              <button type="button" disabled={!selected || !selectedSource}
                aria-label={selectedSource ? `Map response ${selectedSource} to ${target.path}` : `Map response to ${target.path}`}
                title={`Drop or select a response field for ${target.path}`}
                onClick={() => selectedSource && selected && update(target.path, selectedSource)}>
                <Link2 size={16} aria-hidden="true" /><span className="inbound-field-mapper-connection">{source || (selected ? 'Select or drag a response field' : 'Not selected')}</span>
              </button>
            </td>
          </tr>;
        })}
          {!visibleTargets.length && <tr><td colSpan={2}>No response destinations match this filter.</td></tr>}
        </tbody>
      </table></div>
    </SchemaMappingPanels>
  </div>;
}